import React, { useCallback, useState } from 'react';
import { REXY_FONT, REXY_CONSOLE } from '../../lib/rexyConsole';

const MONO = REXY_FONT;

// ─────────────────────────────────────────────────────────────────────────────
// COLLAPSIBLE HOMEPAGE PANELS (owner request, 22 Oct). On the homepage DASHBOARD
// tab, ACTIVITY, NOTES and DREAMSTATE each fold to their header bar and open again
// from a control on that header. Expanded by default, exactly as before; his choice
// for each panel is kept in this browser across refreshes.
//
// OPT-IN BY MOUNT. Only a mount that passes a `collapseKey` gets the control —
// DashboardColumns' DashboardLeft, the homepage. Every other mount (Mission
// Control's ActivityPanel) passes none and renders the DOM it rendered before.
// Display only: the panels fetch and show exactly what they did.
// ─────────────────────────────────────────────────────────────────────────────

const STORE_PREFIX = 'rexy.dashboard.collapsed.';

/** His stored choice for one panel. Storage that cannot be read means expanded. */
function readCollapsed(key: string): boolean {
  try {
    return window.localStorage.getItem(STORE_PREFIX + key) === '1';
  } catch {
    return false;
  }
}

function writeCollapsed(key: string, collapsed: boolean): void {
  try {
    if (collapsed) window.localStorage.setItem(STORE_PREFIX + key, '1');
    else window.localStorage.removeItem(STORE_PREFIX + key);
  } catch {
    // storage unavailable: the choice holds until the page reloads
  }
}

/** [collapsed, toggle] for the panel named by `key`; no key → never collapsed. */
export function useCollapsed(key?: string): [boolean, () => void] {
  const [collapsed, setCollapsed] = useState(() => (key ? readCollapsed(key) : false));
  const toggle = useCallback(() => {
    if (!key) return;
    const next = !collapsed;
    setCollapsed(next);
    writeCollapsed(key, next);
  }, [key, collapsed]);
  return [key ? collapsed : false, toggle];
}

/** The panel's title; with a `collapseKey`, the title beside its fold control. */
export function PanelTitle({ title, style, collapseKey, collapsed, onToggle }: {
  title: string;
  style: React.CSSProperties;
  collapseKey?: string;
  collapsed: boolean;
  onToggle: () => void;
}) {
  if (!collapseKey) return <div style={style}>{'║'} {title}</div>;
  return (
    <div style={styles.titleRow}>
      <div style={style}>{'║'} {title}</div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={!collapsed}
        aria-label={`${collapsed ? 'Expand' : 'Collapse'} ${title}`}
        title={collapsed ? 'Expand' : 'Collapse'}
        style={styles.toggle}
      >
        {collapsed ? '▸' : '▾'}
      </button>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  titleRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, width: '100%', minWidth: 0 },
  toggle: { background: 'transparent', border: `1px solid ${REXY_CONSOLE.cardBorder}`, borderRadius: 4, color: REXY_CONSOLE.dimText, fontFamily: MONO, fontSize: 10, lineHeight: 1, padding: '2px 6px', cursor: 'pointer', flexShrink: 0 },
};
